import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetAgreementCodes, dataSetCommodities, dataSetCountries, dataSetMarkets } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { IAgreementCode } from '../../../@types/model/masterData/agreementCode/agreementCode';
import AgreementCodeHttpService from '../../../services/http/masterData/agreementCodeHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import CommodityHttpService from '../../../services/http/masterData/commodityHttpService';
import CountryHttpService from '../../../services/http/masterData/countryHttpService';
import MarketHttpService from '../../../services/http/masterData/marketHttpService';
import AgreementCodeForm from './form/AgreementCodeForm';
import { setAgreementCodesMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { IAgreementCodeFormValues } from '../../../@types/model/masterData/agreementCode/agreementCodeFormValues';
import AgreementCodeModelHelper from '../../../@types/model/masterData/agreementCode/agreementCodeModelHelper';
import UserHelper from '../../../services/helper/userHelper';
import { useLocation } from 'react-router-dom';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';

const AgreementCodeScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const agreementCodes = useAppSelector(x => x.masterData.agreementCodes);
    const commodities = useAppSelector(x => x.masterData.commodities);
    const countries = useAppSelector(x => x.masterData.countries);
    const markets = useAppSelector(x => x.masterData.markets);
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const location = useLocation();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedAgreementCode, setSelectedAgreementCode] = useState<IAgreementCode | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const [agreementRes, commodityRes, countryRes, marketRes] = await Promise.all([
                        AgreementCodeHttpService.getAgreementCodeData(),
                        CommodityHttpService.getCommodityData(true, true),
                        CountryHttpService.getCountryData(),
                        MarketHttpService.getMarketData(),
                    ]);

                    dispatch(dataSetAgreementCodes(agreementRes.data));
                    dispatch(dataSetCommodities(commodityRes.data));
                    dispatch(dataSetCountries(countryRes.data));
                    dispatch(dataSetMarkets(marketRes.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError | undefined;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading agreement codes.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onSubmit = async (values : IAgreementCodeFormValues) => {
        if (!selectedAgreementCode) return;
        setIsLoading(true);

        try {
            selectedAgreementCode.targetMarketId = (!!values.targetMarket?.value && Number(values.targetMarket.value) > 0) ? Number(values.targetMarket.value) : null;
            const res = await AgreementCodeHttpService.addOrUpdateAgreementCode(selectedAgreementCode);

            const newAgreementCodeList = upsertArrayElement(agreementCodes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetAgreementCodes(newAgreementCodeList));
            await setAgreementCodesMasterDataIndexedDB(newAgreementCodeList);

            generalShowSuccessSnackbar(selectedAgreementCode.id > 0 ? 'Agreement code updated successfully.' : 'Agreement code added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating agreement code data.');
        } finally {
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const edit = (selectedRow : IAgreementCode) => {
        setSelectedAgreementCode(selectedRow);
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedAgreementCode(null);
    };

    const formatCommodities = useCallback((commodityIds : Array<number>) => {
        return commodities
            .filter(x => commodityIds.some(y => y === x.id))
            .map(x => x.code).toString().replace(/,/g, ', ');
    }, [commodities]);

    const formatCountries = useCallback((countryIds : Array<number>) => {
        return countries
            .filter(x => countryIds.some(y => y === x.id))
            .map(x => x.code).toString().replace(/,/g, ', ');
    }, [countries]);

    const getTargetMarket = useCallback((marketId : number) => {
        const market = markets.find(x => x.id === marketId);
        return market ? market.name : '';
    }, [markets]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return AgreementCodeModelHelper.createFormValues(selectedAgreementCode, markets, commodities, countries);
        }
    }, [selectedAgreementCode, markets, commodities, countries, isDialogOpen]);

    /*================================================================================================================
    *                                                  Render Methods
    * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading}>
            <Card className='fdc hfill'>
                <CustomTable<IAgreementCode>
                    
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Agreement Code', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}
    
                    enableSorting
                    enableFiltering
                    enablePagination
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                        { title: 'Start Date', field: 'startDate', enableFiltering: true, enableSorting: true, type: 'date', formatFunction: formatDateTime },
                        { title: 'End Date', field: 'endDate', enableFiltering: true, enableSorting: true, type: 'date', formatFunction: formatDateTime },
                        { title: 'Commodities', field: 'commodityIds', formatFunction: formatCommodities, width: 200, enableFiltering: true, enableSorting: true },
                        { title: 'Countries', field: 'countryIds', formatFunction: formatCountries, width: 200, enableFiltering: true, enableSorting: true },
                        { title: 'Target Market', field: 'targetMarketId', formatFunction: getTargetMarket, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={agreementCodes}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={340}
                    isActive={row => row.isActive}
                />
            </Card>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedAgreementCode ? 'Edit Agreement Code' : 'Add Agreement Code'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'xs'}
                    fullWidth
                >
                    <AgreementCodeForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
        </Screen>
    );
};

export default AgreementCodeScreen;
